import { useEffect, useRef } from 'react';
import { object as yupObject, string as yupString } from 'yup';
import { Formik } from 'formik';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSignupContext } from 'packages/signup/components/signupProvider';
import { Grid, Box } from '@gusto/workbench';
import { Form } from '@gusto/workbench-formik';
import Button from 'components/button';
import setWindowLocationWithParams from 'helpers/set_window_location_with_params';
import {
  IndustryIdentification,
  Intent,
  NumberEmployeesSegmentation,
  PreviousPayrollProvider,
  SurveyQuestionsKeys,
} from 'packages/signup/components/SurveyQuestions';
import type {
  IndustryIdentificationValues,
  IntentStepValues,
  NumberEmployeesSegmentationValues,
  PrevPayrollProviderValues,
  RunPayrollValues,
} from 'packages/signup/components/SurveyQuestions';
import trackViewedPages from 'packages/signup/components/explore/trackViewedPages';
import trackingEvents from 'helpers/trackingEvent';
import TofuSurveyFormObserver from './TofuSurveyFormObserver';

export type TofuSurveyQuestionsType = {
  numberEmployeesSegmentation?: NumberEmployeesSegmentationValues;
  prevPayrollProvider?: PrevPayrollProviderValues;
  industryIdentification?: IndustryIdentificationValues;
  intentStep?: IntentStepValues;
  runPayroll?: RunPayrollValues;
};

export interface TofuSurveyQuestionsValues {
  numberEmployeesSegmentation?: NumberEmployeesSegmentationValues;
  prevPayrollProvider?: PrevPayrollProviderValues;
  industryIdentification?: IndustryIdentificationValues;
  intentStep?: IntentStepValues;
}

export default function TofuSurvey() {
  const numberEmployeesSegmentationRef = useRef(null);
  const prevPayrollProviderRef = useRef(null);
  const industryIdentificationRef = useRef(null);
  const intentStepRef = useRef(null);
  const submitRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    numberEmployeesSegmentation,
    prevPayrollProvider,
    industryIdentification,
    intentStep,
    hideIntentQuestion,
  } = useSignupContext();
  const location = useLocation();

  useEffect(() => {
    trackViewedPages([
      SurveyQuestionsKeys.INDUSTRY_IDENTIFICATION,
      SurveyQuestionsKeys.INTENT_STEP,
      SurveyQuestionsKeys.NUMBER_EMPLOYEES_SEGMENTATION,
      SurveyQuestionsKeys.PREV_PAYROLL_PROVIDER,
    ]);
  }, []);

  const goToNextStep = (shouldRedirectToLearnMore?: boolean) => {
    if (shouldRedirectToLearnMore && !hideIntentQuestion) {
      setWindowLocationWithParams('/invite/company/explore?');
      return;
    }
    const nextStep =
      window.location.pathname.includes('/go/') ||
      window.location.pathname.includes('/partners/invite/')
        ? `${location.pathname}/sign_up_form`
        : '/sign_up_form';
    navigate(searchParams ? `${nextStep}?${searchParams}` : nextStep);
  };

  const onSubmit = (values: TofuSurveyQuestionsValues) => {
    trackingEvents('Button', 'Click', 'submitTofuSurvey', 'Submit');
    goToNextStep(values.intentStep === 'no');
  };

  return (
    <Formik
      initialValues={{
        numberEmployeesSegmentation,
        prevPayrollProvider,
        intentStep,
        industryIdentification,
      }}
      validationSchema={yupObject().shape({
        numberEmployeesSegmentation: yupString().required('Please make a selection'),
        prevPayrollProvider: yupString().required('Please make a selection'),
        industryIdentification: yupString().required('Please make a selection'),
        intentStep: yupString().oneOf(['yes', 'no', '']),
      })}
      onSubmit={onSubmit}
    >
      <Form>
        <TofuSurveyFormObserver />
        <Grid maxWidth={632} autoRows="max-content" rowGapLg="32px" rowGap="24px">
          <NumberEmployeesSegmentation ref={numberEmployeesSegmentationRef} />
          <PreviousPayrollProvider ref={prevPayrollProviderRef} />
          <IndustryIdentification ref={industryIdentificationRef} />

          {!hideIntentQuestion && <Intent ref={intentStepRef} />}

          <Box className="btn-submit-tofu-box" textAlign="center" ref={submitRef}>
            <Button
              className="btn margin-top-10px btn-primary margin-left-none full-width margin-auto btn-submit-tofu"
              type="submit"
              label={
                window.location.href.includes('/partners/invite') ? 'Submit' : 'See Gusto in action'
              }
            />
          </Box>
        </Grid>
      </Form>
    </Formik>
  );
}
